import moment from "moment";

const FRAME_FORMAT = "YYYY-MM-DD HH:mm:ss";
const FRAME_INPUT_FORMATS = ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601];
const FRAME_DATE_FIELDS = ["showstartdate", "start_datetime", "showdate", "dateof", "appointment_date"];

const toFrameMoment = (value) => {
  if (!value) return null;
  // Frame API boundaries and rows are salon wall clocks. Discard an explicit
  // offset without converting its date/time, so all comparisons use one frame.
  const parsed = moment.isMoment(value)
    ? value.clone().utc(true)
    : typeof value === "string"
      ? (/(?:Z|[+-]\d{2}:?\d{2})$/.test(value)
        ? moment.parseZone(value, FRAME_INPUT_FORMATS, true).utc(true)
        : moment.utc(value, FRAME_INPUT_FORMATS, true))
      : moment(value).utc(true);
  return parsed.isValid() ? parsed : null;
};

const firstFrameDate = (record) => {
  if (!record || typeof record !== "object") return null;
  for (const field of FRAME_DATE_FIELDS) {
    const parsed = toFrameMoment(record[field]);
    if (parsed) return parsed;
  }
  return null;
};

/**
 * Resolve the wall-clock date a calendar list-view record belongs to.
 *
 * Calendar rows mix appointments, busy times, working hours and roster rows, so
 * the date can live on the row itself or on the nested appointment the mutation
 * response returns.
 */
export const getRecordFrameDate = (record) => {
  const direct = firstFrameDate(record);
  if (direct) return direct;
  const nested = record?.appointment;
  if (nested && typeof nested === "object" && nested !== record) {
    return firstFrameDate(nested);
  }
  return null;
};

/**
 * The mutation window is the single day that owns the saved appointment. A save
 * therefore never needs the whole visible range back.
 */
export const getAppointmentFrameWindow = (appointment) => {
  const date = getRecordFrameDate(appointment);
  if (!date) return null;
  return {
    start_date: date.clone().startOf("day").format(FRAME_FORMAT),
    end_date: date.clone().add(1, "day").startOf("day").format(FRAME_FORMAT),
  };
};

const unionFrameWindows = (windows) =>
  windows.filter(Boolean).reduce((merged, window) => {
    if (!merged) return window;
    return {
      start_date: moment.min(
        toFrameMoment(merged.start_date),
        toFrameMoment(window.start_date),
      ).format(FRAME_FORMAT),
      end_date: moment.max(
        toFrameMoment(merged.end_date),
        toFrameMoment(window.end_date),
      ).format(FRAME_FORMAT),
    };
  }, null);

/**
 * Union of the mutation windows for one appointment (create/edit) or several
 * appointments (reschedule touches both the previous and the new frame).
 */
export const getFrameWindow = (appointments) => {
  const list = Array.isArray(appointments) ? appointments : [appointments];
  return unionFrameWindows(list.map(getAppointmentFrameWindow));
};

/**
 * Intersect the mutation window with the range the calendar currently shows.
 * Returns null when the updated frame is not on screen, so the calendar does
 * not go to the appointment to refresh it.
 */
export const getScopedRefreshRange = (currentRange, appointments) => {
  const window = getFrameWindow(appointments);
  if (!window) return null;

  const rangeStart = toFrameMoment(currentRange?.start_date);
  const rangeEnd = toFrameMoment(currentRange?.end_date);
  if (!rangeStart || !rangeEnd) return null;

  const windowStart = toFrameMoment(window.start_date);
  const windowEnd = toFrameMoment(window.end_date);
  const start = moment.max(rangeStart, windowStart);
  const end = moment.min(rangeEnd, windowEnd);
  if (!start.isBefore(end)) return null;

  return {
    ...currentRange,
    start_date: start.format(FRAME_FORMAT),
    end_date: end.format(FRAME_FORMAT),
  };
};

const CALENDAR_LIST_PAGE_NAME = "calendar";
const CALENDAR_LIST_FORM_NAME = "calendar/appointment/list";

/**
 * Stamp the calendar list identity onto a mutation refresh request.
 *
 * The backend hides no-show appointments from the calendar only when the
 * appointment view request carries ``page_name=calendar`` and
 * ``formName=calendar/appointment/list``. A frame refresh built from the
 * on-screen range alone loses that identity, so the refreshed frame returns
 * the no-show row and the merge puts it straight back on the grid after the
 * status change. Re-attaching the identity keeps the refresh consistent with
 * the full calendar load.
 */
export const withCalendarListIdentity = (range = {}) => ({
  ...(range || {}),
  page_name: CALENDAR_LIST_PAGE_NAME,
  formName: CALENDAR_LIST_FORM_NAME,
});

export const isRecordInFrameWindow = (record, window) => {
  if (!window) return false;
  const date = getRecordFrameDate(record);
  if (!date) return false;
  const start = toFrameMoment(window.start_date);
  const end = toFrameMoment(window.end_date);
  if (!start || !end) return false;
  return !date.isBefore(start) && date.isBefore(end);
};

const getRecordFrameKey = (record) => {
  if (!record || typeof record !== "object") return null;
  const type = record.listview || record.type || "";
  const id = record.id;
  if (id === undefined || id === null || id === "") {
    const date = getRecordFrameDate(record);
    return date ? `${type}:${date.format(FRAME_FORMAT)}` : null;
  }
  return `${type}:${id}`;
};

/**
 * Merge a frame-scoped response into the calendar data already on screen: only
 * rows inside the refreshed frame are replaced, everything else is kept.
 *
 * Without a window the previous whole-range replacement behaviour is retained.
 */
const INDICATOR_FIELDS = ["calendar_indicators", "is_new_client", "booking_type", "booking_notes",
  "is_requested", "booking_group", "payment", "payments", "resource_item", "resource_allocations",
  "active_subscription_status", "subscription_payment_status", "subscription_purchase_id"];

export const mergeCalendarFrame = (current, refreshed, window, { statusOnly = false } = {}) => {
  const currentList = Array.isArray(current) ? current : [];
  const refreshedList = (Array.isArray(refreshed) ? refreshed : []).map(row => {
    if (!statusOnly) return row;
    // Match the occurrence as well as its id: recurring rows share ids.
    const previous = currentList.find(old => getRecordFrameKey(old) === getRecordFrameKey(row)
      && getRecordFrameDate(old)?.valueOf() === getRecordFrameDate(row)?.valueOf());
    if (!previous) return row;
    const retained = {};
    INDICATOR_FIELDS.forEach(field => {
      if (!Object.prototype.hasOwnProperty.call(row, field)
          && Object.prototype.hasOwnProperty.call(previous, field)) retained[field] = previous[field];
    });
    return { ...retained, ...row };
  });
  if (!window) return refreshedList.length ? refreshedList : currentList;

  const keptRecords = currentList.filter((record) => !isRecordInFrameWindow(record, window));
  const keptKeys = new Set(keptRecords.map(getRecordFrameKey).filter(Boolean));
  const additions = refreshedList.filter((record) => {
    const key = getRecordFrameKey(record);
    return !key || !keptKeys.has(key);
  });

  return [...keptRecords, ...additions];
};

/**
 * Appointments to hand to a frame refresh after a save.
 *
 * A recurring change (this occurrence / this and future) rewrites the series
 * across many days and can re-create it under a new id, so a one-day window
 * would leave the old series' occurrences on screen beside the new ones.
 * Passing no appointments makes the refresh replace the whole visible range.
 */
export const mutationFrameAppointments = (appointments, { recurring = false } = {}) =>
  (recurring ? [] : appointments);

/** True when a save rewrites a repeating series (any scope) rather than one plain appointment. */
export const isRecurringMutation = (values) =>
  Boolean(values) && (
    values.repeat_scope === "individual"
    || values.repeat_scope === "future"
    || values.repeats === "Yes"
  );
